import React from 'react';
import { Card, Tag } from 'antd';
import { CheckCircleFilled, FileTextOutlined } from '@ant-design/icons';

const ImportSummaryStep = ({ accountData }) => {
    const getCategoryCount = (field) => {
        if (Array.isArray(field)) {
            return TB.sumBy(field, fieldName => {
                if (Array.isArray(accountData?.[fieldName])) {
                    return accountData?.[fieldName].length;
                }
                if (!!accountData?.[fieldName] && (
                    accountData?.[fieldName] === true ||
                    (TB.isString(accountData?.[fieldName]) && accountData?.[fieldName]?.length > 0)
                )) {
                    return 1;
                }
                return 0;
            })
        }
        if (Array.isArray(accountData?.[field])) {
            return accountData[field].length;
        }
        if (accountData?.[field]) {
            return 1;
        }
        return 0;
    };

    const categories = [
        {
            name: 'Info',
            icon: 'fas fa-info-circle',
            fields: [
                { key: ['about', 'website', 'is_black_owned', 'is_women_owned'], label: 'About Field' },
                { key: 'profile_links', label: 'Profile Link' },
                { key: 'locations', label: 'Location' }
            ]
        },
        {
            name: 'Brands',
            icon: 'fas fa-handshake',
            fields: [{ key: 'brands', label: 'Brand' }]
        },
        {
            name: 'Work Gallery',
            icon: 'fas fa-images',
            fields: [{ key: 'assets', label: 'Asset' }]
        },
        {
            name: 'Project Highlights',
            icon: 'fas fa-briefcase',
            fields: [{ key: 'key_clients', label: 'Key Client' }]
        },
        {
            name: 'Partner Programs',
            icon: 'fas fa-certificate',
            fields: [
                { key: 'partnerable_programs', label: 'Partner Program' }
            ]
        },
        {
            name: 'Industries',
            icon: 'fas fa-building',
            fields: [{ key: 'industries_worked', label: 'Industry Experience' }]
        },
        {
            name: 'Awards & Certifications',
            icon: 'fas fa-trophy',
            fields: [
                { key: 'awards', label: 'Award' },
                { key: 'certifications', label: 'Certification' }
            ]
        },
        {
            name: 'Service Offerings',
            icon: 'fas fa-cogs',
            fields: [{ key: 'service_offerings', label: 'Service Offering' }]
        }
    ];

    const categoriesWithData = categories.filter(category => {
        return category.fields.some(field => getCategoryCount(field.key) > 0);
    });

    return (
        <div className="py-6">
            {/* Header Section */}
            <div className="flex items-center gap-3 mb-6">
                <div className="h-12 w-12 flex items-center justify-center">
                    <img src="/images/wrex.png" className="w-12 h-12" alt="Wrex" />
                </div>
                <div>
                    <h2 className="text-2xl font-semibold text-gray-900 mb-1">
                        AI Website Parsing Complete!
                    </h2>
                    <p className="text-gray-600 m-0">
                        <b>Wrex</b>, Wripple's AI assistant, has analyzed your website and extracted profile information
                    </p>
                </div>
            </div>

            {/* Success Alert */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6 flex items-start gap-3">
                <CheckCircleFilled className="text-green-600 text-xl mt-0.5" />
                <div>
                    <h3 className="font-semibold text-green-900 mb-1 mt-0">Profile Items Found</h3>
                    <p className="text-green-800 m-0">
                        We've successfully extracted {categoriesWithData.length} categories of information from your website.
                        Review each category in the following steps and select what you'd like to import.
                    </p>
                </div>
            </div>

            {/* Instructions */}
            <Card className="mb-6">
                <h3 className="font-semibold text-lg mb-3 mt-0">How This Works</h3>
                <div className="space-y-3">
                    <div className="flex items-start gap-3">
                        <div className="h-6 w-6 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <span className="text-purple-600 font-semibold text-sm">1</span>
                        </div>
                        <div>
                            <p className="font-medium m-0">Review Each Category</p>
                            <p className="text-gray-600 text-sm m-0">
                                Navigate through each category to see what information was found
                            </p>
                        </div>
                    </div>
                    <div className="flex items-start gap-3">
                        <div className="h-6 w-6 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <span className="text-purple-600 font-semibold text-sm">2</span>
                        </div>
                        <div>
                            <p className="font-medium m-0">Select Items to Import</p>
                            <p className="text-gray-600 text-sm m-0">
                                Choose which items you want to add to your profile (all items are pre-selected)
                            </p>
                        </div>
                    </div>
                    <div className="flex items-start gap-3">
                        <div className="h-6 w-6 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <span className="text-purple-600 font-semibold text-sm">3</span>
                        </div>
                        <div>
                            <p className="font-medium m-0">Review & Import</p>
                            <p className="text-gray-600 text-sm m-0">
                                Review your selections and import them to your profile
                            </p>
                        </div>
                    </div>
                </div>
            </Card>

            {/* Summary of Found Items */}
            <Card>
                <h3 className="font-semibold text-lg mb-4 mt-0">Profile Items Found</h3>
                <div className="space-y-4">
                    {categoriesWithData.map((category, index) => {
                        const totalCount = category.fields.reduce((sum, field) => {
                            return sum + getCategoryCount(field.key);
                        }, 0);

                        return (
                            <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                <div className="flex items-center gap-3">
                                    <div className="h-10 w-10 rounded-full bg-white flex items-center justify-center">
                                        <i className={`${category.icon} text-purple-600`} />
                                    </div>
                                    <div>
                                        <p className="font-medium m-0">{category.name}</p>
                                        <p className="text-sm text-gray-600 m-0">
                                            {category.fields.map((field, idx) => {
                                                const count = getCategoryCount(field.key);
                                                if (count === 0) return null;
                                                return (
                                                    <span key={idx}>
                                                        {idx > 0 && ', '}
                                                        {TB.pluralize(field.label.toLowerCase(),count, true)}
                                                    </span>
                                                );
                                            }).filter(Boolean)}
                                        </p>
                                    </div>
                                </div>
                                <Tag color="purple" className="m-0">
                                    {TB.pluralize('item',totalCount, true)}
                                </Tag>
                            </div>
                        );
                    })}
                </div>
            </Card>

            {categoriesWithData.length === 0 && (
                <div className="text-center py-8">
                    <FileTextOutlined className="text-gray-300 text-5xl mb-4" />
                    <p className="text-gray-500">No profile items were found to import.</p>
                </div>
            )}
        </div>
    );
};

export default ImportSummaryStep;